'use client';

import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from '@headlessui/react';
import Image from 'next/image';
import { useState } from 'react';

import { Checkbox } from '@/shared';
import {
	consentOfProcessingPersonalDataDocument,
	consentToAdvertisingMailingDocument,
	ofertaDocument,
	orderOfProcessingPersonalDataDocument,
	userAgreementDocument,
} from '@/shared/constants';

const MODAL_IMAGE_URL =
	'https://s3.twcstorage.ru/algocode-static/static/assets/images/brand/otus-algocode.svg';

const SUPPORT_EMAIL = 'd.slobodenyuk@algocode.io';

export function LegalEntityConsentModal({
	onAccept,
}: {
	onAccept: (advertisingMail: boolean) => Promise<void>;
}) {
	const [rejected, setRejected] = useState(false);
	const [loading, setLoading] = useState(false);
	const [confirmPolicy, setConfirmPolicy] = useState(false);
	const [confirmOffert, setConfirmOffert] = useState(false);
	const [advertisingMail, setAdvertisingMail] = useState(false);

	const canAccept = confirmPolicy && confirmOffert;

	const handleAccept = async () => {
		setLoading(true);
		try {
			await onAccept(advertisingMail);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open onClose={() => {}} className="relative z-[100]">
			<DialogBackdrop className="fixed inset-0 bg-gray-1/60 backdrop-blur-[2px]" />
			<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
				<DialogPanel className="w-full max-w-[520px] rounded-lg border border-gray-4 bg-white shadow-xl">
					{rejected ? (
						<div className="flex flex-col items-center gap-4 p-8 text-center">
							<DialogTitle className="text-xl font-bold">
								Связаться с нами
							</DialogTitle>
							<p className="text-gray-2">
								По всем вопросам можешь написать на почту:
							</p>
							<span className="text-lg font-semibold">{SUPPORT_EMAIL}</span>
						</div>
					) : (
						<div className="flex flex-col">
							<div className="flex h-[120px] w-full items-center justify-center rounded-t-lg bg-gray-5 px-10">
								<Image
									src={MODAL_IMAGE_URL}
									alt=""
									width={300}
									height={60}
									className="h-auto max-h-[60px] w-auto"
									unoptimized
								/>
							</div>

							<div className="space-y-4 p-6">
								<DialogTitle className="text-xl font-bold">
									Поздравляю!
								</DialogTitle>

								<p className="text-sm leading-relaxed text-gray-2">
									Теперь мы будем делать для тебя ровно в 2 раза больше
									контента! А всё благодаря ООО «Отус Онлайн-Образование».
								</p>

								<p className="text-sm leading-relaxed text-gray-2">
									Мы стали полноценными партнёрами и обновили документы.
									Пожалуйста, подтверди своё согласие с новыми условиями.
								</p>

								<div className="space-y-3">
									<Checkbox
										name="confirmPolicy"
										value="true"
										checked={confirmPolicy}
										onChange={(e) => setConfirmPolicy(e.target.checked)}
										label={
											<>
												Я принимаю условия{' '}
												<a
													href={orderOfProcessingPersonalDataDocument.url}
													className="text-accent hover:underline"
													target="_blank"
												>
													Политики обработки персональных данных
												</a>{' '}
												и{' '}
												<a
													href={userAgreementDocument.url}
													className="text-accent hover:underline"
													target="_blank"
												>
													Пользовательского соглашения
												</a>{' '}
												и даю свое{' '}
												<a
													href={consentOfProcessingPersonalDataDocument.url}
													className="text-accent hover:underline"
													target="_blank"
												>
													согласие на обработку персональных данных
												</a>
											</>
										}
										labelSize="xs"
									/>
									<Checkbox
										name="confirmOffert"
										value="true"
										checked={confirmOffert}
										onChange={(e) => setConfirmOffert(e.target.checked)}
										label={
											<>
												Я согласен с условиями{' '}
												<a
													href={ofertaDocument.url}
													className="text-accent hover:underline"
													target="_blank"
												>
													Оферты
												</a>
											</>
										}
										labelSize="xs"
									/>
									<Checkbox
										name="advertisingMail"
										value="true"
										checked={advertisingMail}
										onChange={(e) => setAdvertisingMail(e.target.checked)}
										label={
											<>
												Я{' '}
												<a
													href={consentToAdvertisingMailingDocument.url}
													className="text-accent hover:underline"
													target="_blank"
												>
													согласен
												</a>{' '}
												получать рассылку с полезными материалами и анонсами.
												Никакого спама, отписаться можно в 1 клик
											</>
										}
										labelSize="xs"
									/>
								</div>

								<div className="flex gap-3 pt-2">
									<button
										onClick={handleAccept}
										disabled={loading || !canAccept}
										className="flex-1 rounded-lg bg-green px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green/90 disabled:opacity-50"
									>
										{loading ? 'Загрузка...' : 'Принимаю'}
									</button>
									<button
										onClick={() => setRejected(true)}
										className="flex-1 rounded-lg border border-gray-4 px-4 py-2.5 text-sm font-semibold text-gray-2 transition-colors hover:bg-gray-5"
									>
										Не принимаю
									</button>
								</div>
							</div>
						</div>
					)}
				</DialogPanel>
			</div>
		</Dialog>
	);
}
